// src/lib/engagement.js
// Phase 37 — extracted from src/App.jsx (lines 470–482, 2326–2453 in the
// post-storage/format/plants/location/products/ai state).
//
// Scope: review-prompt + upgrade-prompt gating, image-report suppression,
//        feature-flag composition for the simulate overlays.
// Public exports: REPORTED_IMAGES_WINDOW_MS, REVIEW_DESTINATIONS,
//                 UPGRADE_TRIGGERS, computeEffectiveFeatures,
//                 defaultReviewPromptState, defaultUpgradePromptState,
//                 getReviewDestination, isEngagementEligible,
//                 isImageReportSuppressed, readReportedImages,
//                 readReviewPromptState, readUpgradePromptState,
//                 shouldShowReviewPrompt, shouldShowUpgrade.
// Module state: none. REVIEW_DESTINATIONS is Object.freeze'd.
//
// Cross-helper deps: depends on src/lib/format.js (one-way; no cycle).

// (No format.js usage in current set — but kept available for the deviceChange
//  trigger work landing later in the phase.)
// import { djb2 } from './format.js';

// Compute effective feature flags — dev simulate premium overrides all flags to true.
// simulateUpgradePrompts (optional, default false): when devModeActive and true,
// forces upgrade_prompts to true so staging can preview prompts with the production flag off (D-16).
// simulateReviewPrompt (optional, default false): Phase 27 ENG-03 — forces review_prompt:true
// when devModeActive and true.
// Phase 27 Plan 03 Gap 2 — the three simulate overlays COMPOSE additively:
//   simulate-premium widens to premium defaults but does NOT touch review_prompt.
//   simulateUpgradePrompts and simulateReviewPrompt overlays apply on top of any simulate-premium base.
//   This replaces the old short-circuit that silently hard-returned review_prompt:false.
export function computeEffectiveFeatures(features, devModeActive, simulatePremium, simulateUpgradePrompts = false, simulateReviewPrompt = false) {
  // Fast path — no simulate active, return features verbatim (no clone).
  if (!devModeActive || (!simulatePremium && !simulateUpgradePrompts && !simulateReviewPrompt)) return features;
  let out = features;
  if (devModeActive && simulatePremium) {
    // Widen to premium defaults — intentionally NOT setting review_prompt here so that
    // the simulateReviewPrompt overlay below can compose on top of it.
    out = { ...out, ask_tend: true, ai_schedule: true, bulk_import: true, colour_enrichment: true, ask_tend_add: true, upgrade_prompts: true };
  }
  if (devModeActive && simulateUpgradePrompts) out = { ...out, upgrade_prompts: true };
  if (devModeActive && simulateReviewPrompt)   out = { ...out, review_prompt: true };
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPGRADE PROMPT PRIMITIVES  (Phase 22 / ONB-03)
// ─────────────────────────────────────────────────────────────────────────────

export const UPGRADE_TRIGGERS = ['plantLimit','unknownPlant','sickPlant','deviceChange','seasonalNudge'];

export function defaultUpgradePromptState() {
  const out = {};
  for (const id of UPGRADE_TRIGGERS) out[id] = { count: 0, lastShownAt: null, dismissedAt: null };
  return out;
}

export function readUpgradePromptState(storage = localStorage) {
  try {
    const raw = storage.getItem('tend:upgradePrompts');
    if (!raw) return defaultUpgradePromptState();
    const parsed = JSON.parse(raw);
    const base = defaultUpgradePromptState();
    for (const id of UPGRADE_TRIGGERS) {
      if (parsed[id] && typeof parsed[id] === 'object') base[id] = { ...base[id], ...parsed[id] };
    }
    return base;
  } catch { return defaultUpgradePromptState(); }
}

// shouldShowUpgrade — returns true ONLY when ALL gates pass (D-15, D-16).
// Pitfall 2: strict ===true check on features.upgrade_prompts (undefined fails).
// forcePreview (optional, default false): Phase 27 Plan 03 Gap 2 — when true AND
// devModeActive, bypasses cap + cooldown gates. The tier gate and flag gate are KEPT —
// premium users never see upgrade prompts; forcePreview only helps when flag is already on.
export function shouldShowUpgrade(triggerId, features, tier, state, now = Date.now(), forcePreview = false) {
  if (tier !== 'free') return false;                       // D-15 tier gate — always kept
  if (features?.upgrade_prompts !== true) return false;    // D-16 kill switch — always kept
  if (forcePreview) return true;                           // dev-mode bypass — cap + cooldown skipped
  const s = (state && state[triggerId]) || { count: 0, lastShownAt: null };
  if (s.count >= 3) return false;                          // D-15 cap
  if (s.lastShownAt && now - s.lastShownAt < 30*24*60*60*1000) return false; // D-15 cooldown
  return true;
}

// Phase 27 ENG-03 — Review prompt infrastructure (SHIPS DARK in v2.2).
// All helpers exported for unit tests. Component and UI wiring lower down.
// Ref: CONTEXT.md D-11..D-18, UI-SPEC Surface B.

export function defaultReviewPromptState() {
  return { count: 0, lastShownAt: null, dismissedAt: null, mutedAt: null };
}

// Phase 34 IMG-01 / D-08 — tend:reportedImages array of {plantId, wiki, reportedAt}.
// Suppresses the "Report wrong image" link for the same plant for 30 days after
// a successful report. Pattern mirrors the Phase 27 tend:troubleShown approach:
// prune entries older than 30 days on read so the in-memory state never bloats.
export const REPORTED_IMAGES_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function readReportedImages(storage = localStorage) {
  try {
    const raw = storage.getItem('tend:reportedImages');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - REPORTED_IMAGES_WINDOW_MS;
    return parsed.filter(e => e && typeof e === 'object' && (e.reportedAt || 0) > cutoff);
  } catch { return []; }
}

export function isImageReportSuppressed(reportedImages, plantId) {
  if (!Array.isArray(reportedImages) || !plantId) return false;
  const cutoff = Date.now() - REPORTED_IMAGES_WINDOW_MS;
  return reportedImages.some(e => e && e.plantId === plantId && (e.reportedAt || 0) > cutoff);
}

export function readReviewPromptState(storage = localStorage) {
  try {
    const raw = storage.getItem('tend:reviewPromptState');
    if (!raw) return defaultReviewPromptState();
    const parsed = JSON.parse(raw);
    return { ...defaultReviewPromptState(), ...(parsed && typeof parsed === 'object' ? parsed : {}) };
  } catch { return defaultReviewPromptState(); }
}

// Composite engagement trigger with bulk-mark-done defence (D-12, D-13).
// Trigger A — >=10 done AND >=3 distinct types AND >=3 distinct calendar days
// Trigger B — >=50 plants
// Trigger C — >=7 distinct session days
// Bulk-mark-done is blocked by the distinct-day gate (Pitfall 4).
export function isEngagementEligible(engagement) {
  if (!engagement || typeof engagement !== 'object') return false;
  const {
    taskCompletions = 0,
    distinctTaskTypes = 0,
    distinctCompletionDays = 0,
    plantCount = 0,
    distinctSessionDays = 0,
  } = engagement;
  if (taskCompletions >= 10 && distinctTaskTypes >= 3 && distinctCompletionDays >= 3) return true;
  if (plantCount >= 50) return true;
  if (distinctSessionDays >= 7) return true;
  return false;
}

// Mirror of shouldShowUpgrade — flag gate, cap, cooldown, dismiss window, mute.
// Pitfall 5: strict `!== true` check on features.review_prompt — undefined fails.
// forcePreview (optional, default false): Phase 27 Plan 03 Gap 2 — when true AND
// devModeActive, bypasses all downstream gates (engagement, cap, cooldown, dismiss,
// mute). The feature-flag gate is KEPT — forcePreview only helps when flag is already on.
export function shouldShowReviewPrompt(features, state, engagement, now = Date.now(), forcePreview = false) {
  if (!features || features.review_prompt !== true) return false;  // flag gate — always kept
  if (forcePreview) return true;  // dev-mode bypass — all subsequent gates skipped
  if (!isEngagementEligible(engagement)) return false;
  const s = state || defaultReviewPromptState();
  if (s.mutedAt) return false;
  if ((s.count || 0) >= 3) return false;
  if (s.lastShownAt && now - s.lastShownAt < 30*24*60*60*1000) return false;
  if (s.dismissedAt && now - s.dismissedAt < 90*24*60*60*1000) return false;
  return true;
}

// Destination resolver. v2.2 web path: review.php (Phase 27 Plan 03 Gap 3 fix — was
// /feedback.php, which is the bug-report form, wrong semantically and 404s on localhost).
// Relative path (no leading slash) so it resolves against the app's base URL —
// matches proxy.php and feedback.php call sites; lets the app live under a subpath
// like /tend-staging/ on the QNAP without the POST escaping the app folder.
// v3.0 (Capacitor Phase 999.6) will add window.Capacitor?.getPlatform() branching to
// fill ios/android slots with real App Store / Play Store URLs.
//
// SINGLE HOME: REVIEW_DESTINATIONS lives ONLY here (not in src/lib/ai.js — per
// WARN 3 in plan 37-02 acceptance criteria).
export const REVIEW_DESTINATIONS = Object.freeze({
  ios:     null,            // TODO v3.0 — App Store URL once Apple Developer account active
  android: null,            // TODO v3.0 — Play Store URL once Google Play Console active
  web:     'review.php',    // Phase 27 Plan 03 Gap 3 — dedicated review capture endpoint (relative — resolves against base URL)
});

export function getReviewDestination() {
  return REVIEW_DESTINATIONS.web;
}
